'use client'

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import Image from 'next/image'

import { SearchParameterKeysTypeEnum } from '@lib/enums/search.enum'
import { useMedusaSearchParametersStore } from '@lib/store/medusa-search/medusa-search-parameters.store'
import { useTranslations } from 'next-intl'

import Cancel from '../../public/cancel.svg'

type Props = {
  onSelection: (location: any) => void
  selectedValue?: any
}

export const GPSSearchInput = forwardRef(function GPSSearchInput(
  { onSelection, selectedValue }: Props,
  ref
) {
  const { removeCurrentUserSearchParameter } = useMedusaSearchParametersStore()
  const [query, setQuery] = useState(selectedValue?.label || '')
  const [results, setResults] = useState<any[]>([])
  const [selectedLocation, setSelectedLocation] = useState<any>(selectedValue)
  const [isLoading, setIsLoading] = useState(false)
  const t = useTranslations('SearchPage')

  useImperativeHandle(ref, () => ({
    clearSelection: () => {
      setSelectedLocation(null)
      setQuery('')
      setResults([])
    },
  }))

  useEffect(() => {
    if (query.length > 2 && !selectedLocation) {
      const delayDebounce = setTimeout(() => {
        fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${query}&accept-language=fr&countrycodes=FR&limit=10`
        )
          .then((res) => res.json())
          .then((data) => setResults(data))
          .catch(console.error)
          .finally(() => setIsLoading(false))
      }, 300)

      setIsLoading(true)

      return () => clearTimeout(delayDebounce)
    } else {
      setResults([])
    }
  }, [query, selectedLocation])

  const handleClearSelection = () => {
    removeCurrentUserSearchParameter(SearchParameterKeysTypeEnum.WHERE_GPS)
    setSelectedLocation(null)
    setQuery('')
    setResults([])
  }

  return (
    <div className="relative">
      <input
        type="text"
        className={`w-full rounded-full border-2 px-4 py-2 pr-10 outline-none ${
          query.trim() !== '' ? 'border-black' : 'border-gray-300'
        } ${selectedLocation ? 'cursor-not-allowed bg-gray-100' : ''}`}
        placeholder={t('searchLocation')}
        value={query}
        disabled={!!selectedLocation}
        onChange={(e) => setQuery(e.target.value)}
      />
      {selectedLocation && (
        <button
          onClick={handleClearSelection}
          className="absolute right-3 top-1/2 -translate-y-1/2"
        >
          <Image src={Cancel} alt="Supprimer" width={33} height={33} />
        </button>
      )}
      {isLoading && !selectedLocation && (
        <p className="ml-1 mt-2 text-sm text-gray-500">Recherche en cours...</p>
      )}
      {results.length > 0 && !selectedLocation && (
        <ul className="absolute z-10 mt-2 max-h-60 w-full overflow-y-auto rounded border bg-white shadow-md">
          {results.map((item) => (
            <li
              key={item.place_id}
              onClick={() => {
                setSelectedLocation(item)
                onSelection(item)
                setQuery(item.display_name)
                setResults([])
              }}
              className="cursor-pointer px-4 py-2 hover:bg-gray-100"
            >
              {item.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
})
